import { useDialogStore } from '@ariakit/react/dialog'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { MINIMUM_IMAGES_REQUIRED } from '@/features/service-vetting/components/service-pro-photos-grid'
import type { RemappedServiceProPhotos } from '@/features/service-vetting/types'
import { ProPhotosHeader } from '@/features/submission-pro-photos-upload/components/pro-photos-header'
import { useTranslation } from '@/translations/i18n'
import { useUpdateProPhotosListing } from '../query/use-update-pro-photos-listing'

interface ServiceProPhotosHeaderProps {
	service: RemappedServiceProPhotos
}

export const ServiceProPhotosHeader = ({
	service
}: ServiceProPhotosHeaderProps) => {
	const { t } = useTranslation()

	const submitModalStore = useDialogStore()
	const submitQuery = useUpdateProPhotosListing(
		{ serviceId: service.id },

		{
			onSuccess: () => {
				toast.success(
					t('vetting:reordering.submit_photo_modal.success_toast')
				)
				submitModalStore.toggle()
			}
		}
	)

	const handleSubmit = () => {
		submitQuery.mutate({ isWaitlisted: false, isHostApprovalNeeded: false })
	}

	const { hasErrors, errorMessage } = useMemo(() => {
		const photoCount = service?.gallery.images.length ?? 0
		const hasOfferingsPhotoMissing = service?.offerings.some(
			(offering) => !offering.mainPhotoUrl
		)
		const hasGalleryCoverPhoto = service?.gallery.images.some(
			(image) => image.isCover
		)

		const photoCountError = photoCount < MINIMUM_IMAGES_REQUIRED
		const coverPhotoError = !hasGalleryCoverPhoto

		const hasErrors =
			photoCountError || hasOfferingsPhotoMissing || coverPhotoError

		if (hasErrors) {
			const errorParts: string[] = []

			if (photoCountError) {
				errorParts.push(
					t('vetting:reordering.gallery_contains_at_least_x_photos', {
						count: MINIMUM_IMAGES_REQUIRED
					})
				)
			}

			if (coverPhotoError) {
				errorParts.push(t('vetting:reordering.cover_selected'))
			}

			if (hasGalleryCoverPhoto) {
				errorParts.push(
					t('vetting:reordering.offering_has_a_selected_photo')
				)
			}

			let errorMessage = t('vetting:reordering.please_ensure_that')

			if (errorParts.length === 1) {
				errorMessage += errorParts[0]
			} else if (errorParts.length === 2) {
				errorMessage += `${errorParts[0]} ${t('common:and')} ${errorParts[1]}`
			} else {
				const lastPart = errorParts.pop()
				errorMessage += `${errorParts.join(', ')} ${t('common:and')} ${lastPart}`
			}

			errorMessage += '.'

			return { hasErrors, errorMessage }
		}

		return { hasErrors, errorMessage: '' }
	}, [service, t])

	const isFieldsChanged = useMemo(() => {
		const isNewPhotoAdded = service?.gallery.images.some(
			(image) => image.isPending
		)
		return service.isUpdateListingNeeded || isNewPhotoAdded
	}, [service])

	return (
		<ProPhotosHeader
			description={t('vetting:reordering.service.description')}
			errorDescription={errorMessage}
			hasErrors={hasErrors}
			isFieldsChanged={isFieldsChanged}
			isPending={submitQuery.isPending}
			onSubmit={handleSubmit}
			submitModalStore={submitModalStore}
			title={t('vetting:reordering.service.title')}
			updatedAt={service.latestUpdate?.updatedAt}
			updatedBy={service.latestUpdate?.userId}
		/>
	)
}
